/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type {
  Airmet,
  CancelAirmet,
  CancelSigmet,
  ProductStatus,
  Sigmet,
} from '@opengeoweb/api';
import type { DrawingListItem } from '../warningsDrawings/types';

export interface Area {
  objectName: string;
  geoJSON: GeoJSON.FeatureCollection;
  areaId?: string;
  published_warning_uuid?: string;
  id?: string;
}

export interface WarningBodyBase {
  id?: string;
  phenomenon: string;
  areas?: Area[];
  validFrom: string;
  validUntil: string;
  level: string;
  probability: number;
  domain?: string;
  warningProposalSource?: string;
  authority?: string;
  type?: number;
  warning_handle_uuid?: string;
  proposal_id?: string;
  linked_to_id?: string;
}

export interface PublicWarningDetail extends WarningBodyBase {
  descriptionOriginal: string;
  descriptionTranslation: string;
  incident?: string;
}

export type AviationWarningDetails =
  | Airmet
  | CancelAirmet
  | Sigmet
  | CancelSigmet;

export enum PublicWarningStatus {
  DRAFT = 'DRAFT',
  DRAFT_PUBLISHED = 'DRAFT_PUBLISHED',
  PUBLISHED = 'PUBLISHED',
  TODO = 'TODO',
  EXPIRED = 'EXPIRED',
  IGNORED = 'IGNORED',
  WITHDRAWN = 'WITHDRAWN',
}

export enum MaritimeWarningAction {
  SELECT = 'SELECT',
  UPDATE = 'UPDATE',
  EXTEND = 'EXTEND',
}

export type MaritimeWarningsType =
  | 'maritimeWarningCoastal'
  | 'maritimeWarningNorthSea';

export type WarningType =
  | 'public'
  | 'airmet'
  | 'sigmet'
  | MaritimeWarningsType
  | 'warningsEvent';

interface BaseWarning {
  status?: PublicWarningStatus;
  productStatus?: ProductStatus;
  editor?: string;
  warningDetail: PublicWarningDetail;
  id?: string;
  lastUpdatedTime?: string;
}

export interface PublicWarning extends BaseWarning {
  type: 'public';
}
export interface AirmetWarning extends BaseWarning {
  type: 'airmet';
  sigmetAirmetDetails: Airmet | CancelAirmet;
}
export interface SigmetWarning extends BaseWarning {
  type: 'sigmet';
  sigmetAirmetDetails: Sigmet | CancelSigmet;
}

export interface MaritimeWarning extends BaseWarning {
  type: MaritimeWarningsType;
  warningDetail: PublicWarningDetail & {
    windDirectionFrom?: string;
    windForce?: string;
    changeType?: string;
    windDirectionTo?: string;
  };
}

export interface EventWarningForWarningList extends BaseWarning {
  type: 'warningsEvent';
  eventId?: string;
  warningDetail: PublicWarningDetail & {
    eventId?: string;
  };
}

export interface WarningEventBody extends WarningBodyBase {
  incident: string;
  eventId?: string;
  status?: PublicWarningStatus;
}

export interface WarningEventTexts {
  headlineOriginal: string;
  headlineTranslation: string;
  conciseSummaryOriginal: string;
  conciseSummaryTranslation: string;
  descriptionOriginal: string;
  descriptionTranslation: string;
}

export enum WarningEventType {
  WARNING = 'WARNING',
  EARLY_WARNING = 'EARLY_WARNING',
}

export enum WarningEventMode {
  ACTUAL = 'ACTUAL',
  TEST = 'TEST',
}
export interface WarningEvent {
  eventId?: string;
  status?: PublicWarningStatus;
  editor?: string;
  lastUpdatedTime?: string;
  phenomenon: string;
  validFrom: string;
  validUntil: string;
  incident: string; // Name of the event
  type?: 'warningsEvent';
  warningEventType: WarningEventType;
  warnings: WarningEventBody[];
  texts: WarningEventTexts;
  linked_to_id?: string;
  mode: WarningEventMode;
  selectedDrawing?: DrawingListItem;
}

export interface WarningEventsConfig {
  // objects assigned to a warning have to lie (partly) within this area
  area_validation_domain?: GeoJSON.FeatureCollection | null;
}

export type Warning =
  | PublicWarning
  | AirmetWarning
  | SigmetWarning
  | MaritimeWarning
  | EventWarningForWarningList;
